import React, { useState, useMemo, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '../App';
import { Calendar, MapPin, Users, Award, Building2, Check, UserPlus } from 'lucide-react';
import { format, parseISO, isBefore, startOfDay } from 'date-fns';
import { formatTimeRange } from '../utils/dateTime';
import { toast } from 'sonner';

const ListView = ({ opportunities, onRefresh }) => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [signingUp, setSigningUp] = useState(null);

  const sortedOpportunities = useMemo(() => {
    return [...opportunities].sort((a, b) => parseISO(a.date) - parseISO(b.date));
  }, [opportunities]);

  const handleQuickSignup = async (e, oppId) => {
    e.stopPropagation();
    setSigningUp(oppId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/signups/${oppId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sign-up request sent! You\'ll be notified once approved.');
      if (onRefresh) onRefresh();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to sign up';
      toast.error(msg);
    } finally {
      setSigningUp(null);
    }
  };

  return (
    <div data-testid="list-view">
      <div className="mb-4">
        <h2 className="text-base md:text-lg font-bold text-[#222222]">
          Upcoming Opportunities ({sortedOpportunities.length})
        </h2>
      </div>

      {sortedOpportunities.length === 0 ? (
        <div className="card text-center py-12" data-testid="no-opportunities-message">
          <p className="text-[#666666]">No upcoming opportunities available.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {sortedOpportunities.map((opp) => {
            const oppDate = parseISO(opp.date);
            const formattedDate = format(oppDate, 'EEEE, MMM d, yyyy');
            const isUnlimited = !opp.capacity || opp.capacity === 0;
            const isFull = !isUnlimited && opp.signups_count >= opp.capacity;
            const spotsLeft = isUnlimited ? null : opp.capacity - opp.signups_count;
            const isPast = isBefore(oppDate, startOfDay(new Date()));
            const isClosed = opp.status === 'closed';
            const isSignedUp = opp.user_signed_up;
            const isVolunteer = user?.role === 'user';
            const canSignUp = isVolunteer && !isSignedUp && !isFull && !isPast && !isClosed;

            return (
              <div
                key={opp.id}
                className="card hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/opportunity/${opp.id}`)}
                data-testid={`list-opp-${opp.id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-1">
                      <h3 className="text-xl font-bold text-[#222222] truncate pr-2">{opp.title}</h3>
                    </div>

                    {opp.partner_org_name && (
                      <div className="flex items-center gap-1.5 mb-3" data-testid={`opp-org-name-${opp.id}`}>
                        <Building2 className="w-3.5 h-3.5 text-[#C9A227] shrink-0" />
                        <span className="text-sm font-medium text-[#8B2E3C]">{opp.partner_org_name}</span>
                      </div>
                    )}

                    <div className="grid sm:grid-cols-2 gap-3 text-sm text-[#666666]">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-[#8B2E3C] shrink-0" />
                        <span>{formattedDate}{opp.start_time ? ` • ${formatTimeRange(opp.start_time, opp.end_time)}` : ''}</span>
                      </div>

                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-[#8B2E3C] shrink-0" />
                        <span className="truncate">{opp.location}</span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-[#8B2E3C] shrink-0" />
                        <span>
                          {isUnlimited ? (
                            `${opp.signups_count} signed up (unlimited spots)`
                          ) : (
                            <>
                              {opp.signups_count} / {opp.capacity}
                              {isFull && <span className="badge badge-full ml-2">Full</span>}
                              {!isFull && spotsLeft <= 3 && spotsLeft > 0 && (
                                <span className="text-[#C9A227] ml-2">({spotsLeft} left)</span>
                              )}
                            </>
                          )}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Award className="w-4 h-4 text-[#8B2E3C] shrink-0" />
                        <span>{opp.credit_hours} hours</span>
                      </div>
                    </div>

                    <p className="text-sm text-[#444444] mt-3 line-clamp-2">
                      {opp.description}
                    </p>
                  </div>

                  <div className="flex flex-row sm:flex-col gap-2 shrink-0">
                    {isVolunteer && (
                      <>
                        {isSignedUp ? (
                          <span
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-[#2D6A4F] text-white"
                            data-testid={`signed-up-badge-${opp.id}`}
                          >
                            <Check className="w-4 h-4" />
                            Signed Up
                          </span>
                        ) : (
                          <button
                            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
                              canSignUp
                                ? 'bg-[#8B2E3C] text-white hover:bg-[#6d2330] active:scale-95'
                                : 'bg-[#E9DDCF] text-[#999999] cursor-not-allowed'
                            }`}
                            disabled={!canSignUp || signingUp === opp.id}
                            onClick={(e) => handleQuickSignup(e, opp.id)}
                            data-testid={`quick-signup-btn-${opp.id}`}
                          >
                            <UserPlus className="w-4 h-4" />
                            {signingUp === opp.id ? 'Signing Up...' : isFull ? 'Full' : isPast ? 'Past' : isClosed ? 'Closed' : 'Quick Sign Up'}
                          </button>
                        )}
                      </>
                    )}
                    <button
                      className="btn-outline whitespace-nowrap text-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/opportunity/${opp.id}`);
                      }}
                      data-testid={`view-details-button-${opp.id}`}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ListView;
